<i18n>
{
  "en": {
    "createSaleRateIndexingRuleTitle": "Create sale rate indexing rule",
    "emptyFieldError": "Mandatory field",
    "invalidNumberError": "Must be a valid number",
    "isPercentage": "Add a percentage to the reference price",
    "isFixed": "Add a value to the reference price",
    "isActive": "Enable rule for future price recommendation generations",
    "fixedIncrement": "Fixed increment",
    "percentageIncrement": "Percentage increment",
    "selectReferenceRate": "Select reference sale rate",
    "selectChangedRate": "Select changed sale rate",
    "saleRateIndexingRuleSubtitle": "Links the prices of the target rate to the reference rate until the rule is disabled",
    "oneOptionRequired": "Select at least one of the following options"
  },
  "es": {
    "createSaleRateIndexingRuleTitle": "Crea una regla de indexación de tarifa",
    "emptyFieldError": "Campo obligatorio",
    "invalidNumberError": "Tiene que ser un número válido",
    "isPercentage": "Sumar un porcentaje al precio de referencia",
    "isFixed": "Sumar valor al precio de referencia",
    "isActive": "Activar regla para futuras generaciones de recomendaciones de precios",
    "fixedIncrement": "Incremento fijo",
    "percentageIncrement": "Incremento de porcentaje",
    "selectReferenceRate": "Selecciona tarifa de referencia",
    "selectChangedRate": "Selecciona tarifa objetivo",
    "saleRateIndexingRuleSubtitle": "Enlaza los precios de la tarifa objetivo a los de la tarifa de referencia hasta que se desactive la regla",
    "oneOptionRequired": "Selecciona al menos una de las siguientes opciones"
  }
}
</i18n>

<template>
  <v-dialog @click:outside="onCancel" v-model="visible" max-width="600px" scrollable persistent>
    <v-card v-if=visible height="100%" class="px-7 py-5">
      <div class="d-flex align-center justify-space-between">
        <v-card-title class="word-break pa-0">
          {{ $t('createSaleRateIndexingRuleTitle') }}
        </v-card-title>
      </div>
      <v-card-subtitle class="pa-0">{{ $t('saleRateIndexingRuleSubtitle') }}</v-card-subtitle>
      <v-container class="pa-0">
        <v-form v-model="valid" class="d-flex flex-column align-left" ref="form">
          <v-select v-model="referenceSaleRate" :items="managedSaleRates"
                    :menu-props="{ maxHeight: '400' }" :label="$t('selectReferenceRate')"
                    item-value="guid" item-text="name" class="required" :rules="objectRules"/>
          <v-select v-model="changedSaleRate" :items="unmanagedSaleRates"
                    :menu-props="{ maxHeight: '400' }" :label="$t('selectChangedRate')"
                    item-value="guid" item-text="name" class="required" :rules="objectRules"/>
          <v-divider/>
          <v-card-subtitle class="pa-0 ma-0 mt-2 text-center">{{ $t('oneOptionRequired') + ' *' }}</v-card-subtitle>
          <v-switch v-model="isFixed" dense :label="$t('isFixed')" @change="onFixed"/>
          <v-text-field v-if="isFixed && !isPercentage" v-model="fixedIncrement" :rules="numberRules"
                        :label="$t('fixedIncrement')"
                        :prefix="currency.hasLeftPosition() ? currency.symbol : ''"
                        :suffix="currency.hasRightPosition() ? currency.symbol : ''"
                        required/>
          <v-switch v-model="isPercentage" dense :label="$t('isPercentage')" @change="onPercentage"/>
          <v-text-field v-if="isPercentage && !isFixed" v-model="percentageIncrement"
                        :rules="numberRules" :label="$t('percentageIncrement')" suffix="%" required/>
          <v-divider/>
          <v-switch v-model="isActive" dense :label="$t('isActive')"/>
          <dynameat-modal-buttons @cancel="onCancel" @confirm="onConfirm" :confirmDisabled="!formValid"
            :confirmText="$t('confirm')"/>
        </v-form>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>

import { notEmptyObject, notValidNumberNegative } from '@core/helpers/formValidators';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import { Components, MapGetters } from '@core/helpers/decorators';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import { SaleRateIndexingRule } from '../models';

@Component
@Components({ DynameatModalButtons })
@MapGetters(['saleRateList', 'currency'])
export default class SaleRateIndexingRuleModal extends Vue {
  @Prop({ type: Boolean, default: false }) visible;

  valid = false;

  percentageIncrement = null;

  fixedIncrement = null;

  changedSaleRate = null;

  referenceSaleRate = null;

  isActive = true;

  isFixed = false;

  isPercentage = false;

  created() {
    this.referenceSaleRate = undefined;
    this.changedSaleRate = undefined;
  }

  @Watch('visible')
  async onVisibleChange() {
    this.clearOwnData();
  }

  get managedSaleRates() {
    return this.saleRateList.filter((rate) => rate.is_managed);
  }

  get unmanagedSaleRates() {
    return this.saleRateList.filter((rate) => !rate.is_managed);
  }

  get objectRules() {
    return [notEmptyObject(this.$t('emptyFieldError'))];
  }

  get numberRules() {
    if (this.isFixed || this.isPercentage) {
      return [notValidNumberNegative(this.$t('invalidNumberError'))];
    }
    return [];
  }

  get formValid() {
    return this.valid && ((this.isPercentage && !this.isFixed) || (this.isFixed && !this.isPercentage));
  }

  onFixed() {
    this.isPercentage = false;
    this.percentageIncrement = null;
  }

  onPercentage() {
    this.isFixed = false;
    this.fixedIncrement = null;
  }

  buildSaleRateIndexingRule() {
    const isNotPercentageIncrementValid = Number.isNaN(this.percentageIncrement) || this.percentageIncrement === null;
    return SaleRateIndexingRule.toInstance({
      guid: null,
      percentage_increment: (isNotPercentageIncrementValid) ? null : this.percentageIncrement / 100,
      fixed_increment: this.fixedIncrement,
      is_active: this.isActive,
      changed_sale_rate: this.changedSaleRate,
      reference_sale_rate: this.referenceSaleRate
    });
  }

  onConfirm() {
    if (this.formValid) {
      const saleRateIndexingRule = this.buildSaleRateIndexingRule();
      this.$emit('confirm', saleRateIndexingRule);
    }
  }

  onCancel() {
    this.$emit('cancel');
  }

  clearOwnData() {
    this.percentageIncrement = null;
    this.fixedIncrement = null;
    this.changedSaleRate = null;
    this.referenceSaleRate = null;
    this.isActive = true;
    this.isPercentage = true;
    this.isFixed = false;
  }
}
</script>
<style scoped>
.progress-enter-active, .progress-leave-active {
  transition: opacity .5s
}

.progress-enter, .progress-leave-to /* .fade-leave-active below version 2.1.8 */
{
  opacity: 0
}

.word-break {
  word-break: break-word;
}

.required label::after {
  content: " *";
  color: red;
}
</style>
